import React, { FC, useMemo, useState } from 'react';
import { Box, Chip, Paper, TableContainer } from '@mui/material';
import { UseCollectionHookResult } from '../../../hooks/useCollection';
import TableErrorComponent from '../../General/TableErrorComponent';
import { Spinner } from '../../General/Spinner';
import {
  entitlementRowComparator,
  getEarliestValidFromDateInClaims,
  getEntitlementIsActiveCellStyle,
  getEntitlementValidFromCellStyle,
  getEntitlementValidToCellStyle,
  getLatestValidToDateFromClaims,
  isDateInFuture,
  isDateInPast,
  isExpandableEntitlement,
} from '../../../util/subscribedAccessUtils';
import ExpandableCustomTable from '../../General/ExpandableTable/ExpandableCustomTable';
import { PaginationInfo } from '../../../types/table';
import { isEditableEntitlement, renderDateString } from '../../../util/utils';
import { useAuth } from '../../../providers/AuthProvider';
import { Claim, Entitlement } from '../../../types/entitlements';
import { ClaimRowData, EntitlementRowData } from './entitlements-ui-types';
import { entitlementRowCellConfig } from './EntitlementRowCellConfig';
import ClaimsTable from './ClaimsTable';
import { Cancel, CheckCircle, SvgIconComponent } from '@mui/icons-material';
import entitlementTableHeaderCells from './EntitlementsHeaderCells';
import claimRowHeadCells from './ClaimsTableHeaderCells';
import { TooltipCellConfig } from '../../General/Table/table-ui-types';

interface EntitlementsTableProps {
  collectionHook: UseCollectionHookResult<Entitlement>;
  onEdit: (data: EntitlementRowData) => void;
}

function mapEntitlementsToRowData(entitlements: Entitlement[]): EntitlementRowData[] {
  return entitlements.map((entitlement) => ({
    ...entitlement,
    ...entitlement.id,
    claims: entitlement.claims.map((e: Claim) => {
      const invalidityReason = isDateInFuture(e.validFrom)
        ? 'upcoming'
        : isDateInPast(e.validTo)
          ? 'expired'
          : undefined;
      return {
        ...e,
        invalidityReason,
      };
    }),
    earliestValidFrom: getEarliestValidFromDateInClaims(entitlement),
    latestValidTo: getLatestValidToDateFromClaims(entitlement),
  }));
}

// Responsible for coordinating the display of entitlements data within a table
const EntitlementsTableContainer: FC<EntitlementsTableProps> = ({ collectionHook, onEdit }) => {
  const { collection, hasErrors, isLoading } = collectionHook;
  const { roles } = useAuth();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const paginationInfo: PaginationInfo = useMemo(
    () => ({
      pageNumber: page,
      pageSize: rowsPerPage,
      setPageNumber: setPage,
      setPageSize: setRowsPerPage,
    }),
    [page, rowsPerPage]
  );

  const entitlementRows = useMemo(() => mapEntitlementsToRowData(collection || []), [collection]);

  return (
    <TableContainer component={Paper}>
      {!hasErrors && !isLoading && (
        <ExpandableCustomTable<EntitlementRowData>
          rowCellConfig={entitlementRowCellConfig}
          collection={entitlementRows}
          comparator={entitlementRowComparator}
          headerCells={entitlementTableHeaderCells}
          GenericExpandableTableComponent={ClaimsTable}
          paginationInfo={paginationInfo}
          isEditable={(row) => isEditableEntitlement(row, roles)}
          handleEditClick={onEdit}
          isExpandable={isExpandableEntitlement}
          expandableTableHeadCells={claimRowHeadCells}
          expendableTableRowConfig={claimRowCellConfig}
        />
      )}
      {hasErrors && (
        <TableErrorComponent message={'An error occurred, data could not be loaded.'} />
      )}
      {!hasErrors && isLoading && <Spinner></Spinner>}
    </TableContainer>
  );
};

const renderClaimStatus = (claim: ClaimRowData) => {
  const IsActiveIcon: SvgIconComponent = claim.isActive ? CheckCircle : Cancel;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
      <IsActiveIcon />
      {claim.invalidityReason && (
        <Chip
          size="small"
          color={claim.invalidityReason === 'expired' ? 'warning' : 'info'}
          label={claim.invalidityReason}
        />
      )}
    </Box>
  );
};
export const claimRowCellConfig: TooltipCellConfig<ClaimRowData>[] = [
  { title: 'Brand', propertyKey: 'brand', width: 1, showTooltip: false },
  { title: 'Code', propertyKey: 'code', width: 1, showTooltip: false },
  {
    title: 'IsActive',
    propertyKey: 'isActive',
    render: renderClaimStatus,
    style: getEntitlementIsActiveCellStyle,
    width: 1,
    showTooltip: false,
  },
  {
    title: 'Valid From',
    propertyKey: 'validFrom',
    render: renderDateString,
    style: getEntitlementValidFromCellStyle,
    width: 2,
  },
  {
    title: 'Valid To',
    propertyKey: 'validTo',
    render: renderDateString,
    style: getEntitlementValidToCellStyle,
    width: 2,
  },
];

export default EntitlementsTableContainer;
